import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { ReasonDialog } from "@/components/payments/ReasonDialog";
import { api, cedi2 as cedi, errMsg, fmtDate, showPin } from "@/lib/api";

export const VoidedPayments = ({ eventId, canDelete, tick }) => {
  const [list, setList] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/voided-payments", { params: { event_id: eventId } }).then((r) => setList(r.data)).catch((e) => { toast.error(errMsg(e)); setList([]); }), [eventId]);
  useEffect(() => { load(); }, [load, tick]);
  const purge = async (reason) => {
    await api.delete(`/voided-payments/${del.id}`, { data: { reason } });
    toast.success(`${del.receipt_no} permanently deleted`);
    setDel(null);
    load();
  };
  if (!list) return <div className="text-gray-500 text-sm">Loading…</div>;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto" data-testid="voided-payments-panel">
      <table className="w-full text-sm" data-testid="voided-payments-table">
        <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500">
          <tr>{["Receipt No.", "Ticket", "Participant", "Method", "Amount", "Paid on", "Voided", "Reason", ""].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y">{list.map((p) => (
          <tr key={p.id} data-testid={`voided-payment-row-${p.receipt_no}`}>
            <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">{p.receipt_no}</td>
            <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{p.ticket_no}</td>
            <td className="px-4 py-3 whitespace-nowrap">{p.delegate_name}<div className="font-mono text-[11px] text-gray-500">{showPin(p.pin)}</div></td>
            <td className="px-4 py-3 whitespace-nowrap">{p.method}</td>
            <td className="px-4 py-3 font-mono text-xs line-through text-gray-500 whitespace-nowrap">{cedi(p.amount)}</td>
            <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(p.created_at)}</td>
            <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(p.voided_at)}<div className="text-gray-500">{p.voided_by}</div></td>
            <td className="px-4 py-3 text-xs min-w-[180px]">{p.void_reason}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap">{canDelete && <button onClick={() => setDel(p)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#E60000] hover:underline" data-testid={`voided-payment-delete-${p.receipt_no}`}><Trash2 className="w-3.5 h-3.5" />Delete permanently</button>}</td>
          </tr>))}</tbody>
      </table>
      {list.length === 0 && <div className="p-10 text-center text-sm text-gray-500" data-testid="voided-payments-empty">No voided receipts for this event.</div>}
      {del && <ReasonDialog title={`Delete ${del.receipt_no} permanently`} desc="This voided receipt is erased for good and cannot be recovered. The registration's amount paid does not change." confirmLabel="Delete permanently" onConfirm={purge} onClose={() => setDel(null)} testId="voided-payment-delete-dialog" />}
    </div>
  );
};

export const VoidPaymentList = ({ t, onVoided }) => {
  const [p, setP] = useState(null);
  const pays = t.payments || [];
  const lastRefund = pays.map((x) => x.type).lastIndexOf("refund");
  const items = pays.filter((x, i) => i > lastRefund && x.type !== "refund" && !x.cheque_id);
  if (!items.length || t.event_archived || t.event_deleted) return null;
  const go = async (reason) => {
    const r = await api.post(`/transactions/${t.id}/payments/${p.id}/void`, { reason });
    toast.success(`${p.receipt_no} voided`);
    setP(null);
    onVoided(r.data);
  };
  return (
    <div className="rounded-md border bg-[#F8F9FA] p-3 text-xs" data-testid="void-payment-list">
      <div className="font-bold uppercase tracking-wider text-[10px] text-gray-500 mb-2">Staff only · void a payment receipt</div>
      {items.map((x) => (
        <div key={x.id} className="flex items-center justify-between gap-2 py-1">
          <span className="font-mono">{x.receipt_no} · {x.method} · {cedi(x.amount)}</span>
          <button onClick={() => setP(x)} className="font-semibold text-gray-700 hover:text-[#E60000] hover:underline" data-testid={`void-payment-${x.receipt_no}`}>Void</button>
        </div>
      ))}
      {p && <ReasonDialog title={`Void ${p.receipt_no}`} desc={`${cedi(p.amount)} is taken off what this participant has paid and the receipt moves to the Voided receipts tab.`} confirmLabel="Void receipt" onConfirm={go} onClose={() => setP(null)} testId="void-payment-dialog" />}
    </div>
  );
};
